// ecolabJerarquia — lee la estructura de la red desde Ecolab/Fenagro DESDE EL
// NAVEGADOR y la deja lista para la importación TRANSITORIA a CoopNet:
// confederación internacional, confederaciones nacionales, federaciones y
// cooperativas, cada una con los datos de detalle que Ecolab ya persiste.
//
// Por qué desde el navegador y no desde el backend de CoopNet: el filtro
// global ValidateOriginFilter de Ecolab responde 401 a toda petición sin
// cabecera Origin salvo a rutas exentas (geo, auth, asociado...);
// /api/ecolab/jerarquia/* no está exenta, así que una llamada de servidor a
// servidor siempre da 401 aunque el token sea válido. El navegador sí envía
// Origin, y ese origen debe estar en la lista permitida de Ecolab (hoy
// localhost:3050; el dominio de producción de CoopNet habrá que agregarlo allá).
//
// Endpoints usados:
//   • jerarquia/arbol            → ids y parentescos (OBLIGATORIO)
//   • jerarquia/confederacion    → internacional completa + sus nacionales
//   • jerarquia/federaciones     → contacto, límite y estado de cada federación
//   • cooperativas               → contacto, límites y porcentajes de cada cooperativa
// Los tres últimos son opcionales: si alguno falla, se importa igual solo con
// el árbol (y se avisa en consola). Nunca se inventa un dato que Ecolab no trae.

import { getToken } from './apiClient';

const ECOLAB_BASE_URL = process.env.REACT_APP_ECOLAB_BASE_URL;

async function pedir(ruta, obligatorio) {
  let res;
  try {
    res = await fetch(`${ECOLAB_BASE_URL}${ruta}`, { headers: { Authorization: `Bearer ${getToken()}` } });
  } catch {
    if (!obligatorio) return null;
    throw new Error(
      `No se pudo conectar con Ecolab. Si el navegador bloqueó la petición (CORS), revisa que el origen ` +
        `${window.location.origin} esté permitido en Ecolab.`
    );
  }
  if (res.status === 401 && obligatorio) {
    throw new Error(
      `Ecolab rechazó la petición (401). Su filtro solo acepta orígenes permitidos: revisa que ` +
        `${window.location.origin} esté en su lista. Si el origen es correcto, cierra sesión y vuelve a entrar.`
    );
  }
  if (!res.ok) {
    if (obligatorio) throw new Error(`Ecolab respondió ${res.status} al leer la estructura de la red.`);
    console.warn(`Ecolab respondió ${res.status} en ${ruta}: se importa sin ese detalle.`);
    return null;
  }
  return res.json().catch(() => null);
}

const arr = (x) => (Array.isArray(x) ? x : []);
/** Lee un campo sin importar si Ecolab responde en camelCase o PascalCase. */
const leer = (o, k) => (o ? o[k] ?? o[k.charAt(0).toUpperCase() + k.slice(1)] : undefined);
const indexar = (lista, clave) => new Map(arr(lista).map((x) => [leer(x, clave), x]));

/**
 * @returns {Promise<{
 *   confederacionInternacional: object|null,
 *   confNacionales: object[], federaciones: object[], cooperativas: object[]
 * }>}
 */
export async function obtenerEstructuraEcolab() {
  const arbol = await pedir('/api/ecolab/jerarquia/arbol', true);
  const [intl, feds, coops] = await Promise.all([
    pedir('/api/ecolab/jerarquia/confederacion', false),
    pedir('/api/ecolab/jerarquia/federaciones', false),
    pedir('/api/ecolab/cooperativas', false),
  ]);

  // ── Confederación internacional ──
  const ci = intl
    ? {
        confIntID: leer(intl, 'confIntID'),
        nombre: leer(intl, 'nombre'),
        siglas: leer(intl, 'siglas'),
        representante: leer(intl, 'representante'),
        email: leer(intl, 'email'),
        telefono: leer(intl, 'telefono'),
        sitioWeb: leer(intl, 'sitioWeb'),
        mision: leer(intl, 'mision'),
        vision: leer(intl, 'vision'),
        logoUrl: leer(intl, 'logoUrl'),
        paisID: leer(intl, 'paisID'),
        direccion: leer(intl, 'direccion'),
        fechaFundacion: leer(intl, 'fechaFundacion'),
        estado: leer(intl, 'estado'),
      }
    : null;

  // ── Confederaciones nacionales: el árbol manda; el detalle sale de la internacional ──
  const detalleNac = indexar(leer(intl, 'confederacionesNacionales'), 'confNacID');
  const confNacionales = arr(leer(arbol, 'confNacionales')).map((c) => {
    const id = leer(c, 'confNacID');
    const d = detalleNac.get(id);
    return {
      confNacID: id,
      // Ecolab tiene una sola internacional: si el detalle no la trae, se enlaza a esa.
      confIntID: leer(d, 'confIntID') ?? ci?.confIntID ?? null,
      nombre: leer(c, 'nombre'),
      siglas: leer(c, 'siglas') ?? leer(d, 'siglas'),
      pais: leer(c, 'pais'), // en el árbol es el NOMBRE del país
      paisId: leer(d, 'paisID') ?? null,
      representante: leer(d, 'representante'),
      email: leer(d, 'email'),
      telefono: leer(d, 'telefono'),
      sitioWeb: leer(d, 'sitioWeb'),
      direccion: leer(d, 'direccion'),
      logoUrl: leer(d, 'logoUrl'),
      fechaConstitucion: leer(d, 'fechaConstitucion'),
      estado: leer(d, 'estado'),
    };
  });

  // ── Federaciones ──
  const detalleFed = indexar(feds, 'federacionID');
  const federaciones = arr(leer(arbol, 'federaciones')).map((f) => {
    const id = leer(f, 'federacionID');
    const d = detalleFed.get(id);
    return {
      federacionID: id,
      confNacID: leer(f, 'confNacID'),
      nombre: leer(f, 'nombre'),
      siglas: leer(f, 'siglas'),
      esp: leer(f, 'esp'),
      email: leer(d, 'email'),
      logoUrl: leer(d, 'logoUrl'),
      maxCooperativas: leer(d, 'maxCooperativas'),
      estado: leer(d, 'estado'),
    };
  });

  // ── Cooperativas ──
  const detalleCoop = indexar(coops, 'cooperativaID');
  const cooperativas = arr(leer(arbol, 'cooperativas')).map((c) => {
    const id = leer(c, 'cooperativaID');
    const d = detalleCoop.get(id);
    return {
      cooperativaID: id,
      federacionID: leer(c, 'federacionID'),
      nombre: leer(c, 'nombre'),
      siglas: leer(c, 'siglas') ?? leer(d, 'siglas'),
      email: leer(d, 'email'),
      telefono: leer(d, 'telefono'),
      maxAfiliados: leer(d, 'maxAfiliados'),
      porcentajeWallet: leer(d, 'porcentajeWallet'),
      porcentajeFed: leer(d, 'porcentajeFed'),
      porcentajeBanco: leer(d, 'porcentajeBanco'),
      fechaConstitucion: leer(d, 'fechaConstitucion'),
    };
  });

  return { confederacionInternacional: ci, confNacionales, federaciones, cooperativas };
}

// ─────────────────────────────────────────────────────────────
// Afiliados ACTIVOS de cada cooperativa (importación transitoria)
// ─────────────────────────────────────────────────────────────
// Ecolab entrega los afiliados POR cooperativa (el listado exige cooperativaId) y en dos niveles:
//   • GET afiliados?cooperativaId&estado=ACTIVO&page&pageSize → resumen (nombres, documento, contacto)
//   • GET afiliados/{id}                                       → detalle (ubicación, nacimiento, aporte, usuario)
// Solo se traen los ACTIVOS: los PENDIENTE, EN_ORIENTACION o RECHAZADO siguen en el embudo de afiliación
// de Ecolab y todavía no son afiliados. Si el detalle de alguno falla, se usa su resumen.
//
// @param {number[]} idsCooperativas  ids de las cooperativas EN ECOLAB (los del árbol)
// @param {(texto: string) => void} onProgreso
// @returns {Promise<{afiliados: object[], cooperativasSinLeer: number, detallesFallidos: number}>}
export async function obtenerAfiliadosEcolab(idsCooperativas, onProgreso = () => {}) {
  const resumenes = [];
  let cooperativasSinLeer = 0;

  for (const [i, cooperativaID] of idsCooperativas.entries()) {
    onProgreso(`Leyendo afiliados de la cooperativa ${i + 1}/${idsCooperativas.length}…`);
    let pagina = 1;
    let total = Infinity;
    let leyo = false;
    while ((pagina - 1) * 100 < total) {
      const r = await pedir(`/api/ecolab/afiliados?cooperativaId=${cooperativaID}&estado=ACTIVO&page=${pagina}&pageSize=100`, false);
      if (!r) break;
      leyo = true;
      total = Number(leer(r, 'total')) || 0;
      arr(leer(r, 'data')).forEach((a) => resumenes.push({ ...a, cooperativaID }));
      pagina += 1;
    }
    if (!leyo) cooperativasSinLeer += 1;
  }

  // Detalle de cada afiliado, con concurrencia limitada para no saturar a Ecolab.
  const detalles = new Map();
  const cola = [...resumenes];
  let hechos = 0;
  const trabajador = async () => {
    while (cola.length) {
      const s = cola.shift();
      const id = leer(s, 'afiliadoID');
      const d = await pedir(`/api/ecolab/afiliados/${id}`, false);
      if (d) detalles.set(id, d);
      hechos += 1;
      onProgreso(`Leyendo el detalle de los afiliados ${hechos}/${resumenes.length}…`);
    }
  };
  await Promise.all(Array.from({ length: 6 }, trabajador));

  const afiliados = resumenes.map((s) => {
    const id = leer(s, 'afiliadoID');
    const d = detalles.get(id);
    const de = (k) => leer(d, k) ?? leer(s, k);
    const td = leer(d, 'tipoDocumento');
    return {
      afiliadoID: id,
      cooperativaID: s.cooperativaID,
      aspNetUserID: leer(d, 'aspNetUserID'),
      nombres: de('nombres'),
      apellidos: de('apellidos'),
      documento: de('documento'),
      tipoDocumento: typeof td === 'string' ? td : leer(td, 'codigo'),
      email: de('email'),
      telefono: de('telefono'),
      direccion: leer(d, 'direccion'),
      genero: leer(d, 'genero'),
      estadoCivil: leer(d, 'estadoCivil'),
      paisID: leer(d, 'paisID'),
      departamentoID: leer(d, 'departamentoID'),
      municipioID: leer(d, 'municipioID'),
      fechaNacimiento: leer(d, 'fechaNacimiento'),
      fechaAfiliacion: de('fechaAfiliacion'),
      valorAporteMensual: leer(d, 'valorAporteMensual'),
    };
  });

  return { afiliados, cooperativasSinLeer, detallesFallidos: resumenes.length - detalles.size };
}
